import React from 'react';
import './About.css';

function About() {
  return (
    <div className="about-page">
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            <span className="page-subtitle">About Us</span>
            <h1 className="page-title">Transforming Young Lives Through Pediatric Surgery</h1>
            <p className="page-description">
              Since 2004, DOC Humanitarian has been providing life-changing pediatric surgery & urology 
              to children in Cambodia who need complex procedures unavailable in their country.
            </p>
          </div>
        </div>
      </section>

      <section className="about-content section">
        <div className="container">
          <div className="content-grid">
            <div className="content-text">
              <h2 className="content-title">Our Story</h2>
              <p className="content-paragraph">
                DOC Humanitarian (D.O.C. HUMANITARIAN INC) was originally founded in France 
                around 2004–2005 under a different name. From the beginning, our purpose has 
                been clear: to help children who need complex surgical procedures that cannot 
                be safely performed in the countries where they live, due to limited medical 
                resources and infrastructure.
              </p>
              <p className="content-paragraph">
                Our work focuses specifically on pediatric surgery & urology. We partner closely 
                with the National Pediatric Hospital in Phnom Penh, Cambodia, where children 
                are first evaluated locally by the hospital team. The most complex cases are 
                then presented to our group of surgeons, and together we organize missions that 
                bring the necessary equipment, specialized skills, and coordinated care to 
                operate on as many eligible children as possible during each visit.
              </p>
              <p className="content-paragraph">
                The association was initially led by its founding president and a vice-president 
                who acted as his right hand. Over time, as the founding president stepped away, 
                the vice-president continued the work and leadership of the organization. With 
                the current president now based in the United States, the association is being 
                formally established here while maintaining our humanitarian mission and 
                international partnerships.
              </p>
            </div>
            <div className="content-image">
              <img src="/images/about-team.jpg" alt="DOC Humanitarian medical team" />
            </div>
          </div>
        </div>
      </section>

      <section className="mission-structure section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">How We Work</span>
            <h2 className="section-title">Our Mission Structure</h2>
            <p className="section-description">
              Each mission is carefully planned and executed in three distinct phases 
              to ensure the best possible outcomes for our young patients.
            </p>
          </div>

          <div className="structure-grid">
            <div className="structure-card">
              <div className="structure-number">1-2</div>
              <h3 className="structure-title">Missions Per Year</h3>
              <p className="structure-description">
                We typically carry out one mission per year, occasionally two, each lasting 
                approximately two weeks.
              </p>
            </div>

            <div className="structure-card">
              <div className="structure-number">40-50</div>
              <h3 className="structure-title">Children Per Mission</h3>
              <p className="structure-description">
                During each mission, we perform surgery on approximately 40 to 50 children 
                with complex medical conditions.
              </p>
            </div>

            <div className="structure-card">
              <div className="structure-number">3</div>
              <h3 className="structure-title">Mission Phases</h3>
              <p className="structure-description">
                Pre-operative evaluation, operative procedures, and post-operative care 
                and recovery monitoring.
              </p>
            </div>
          </div>

          <div className="phases-content">
            <h3 className="phases-title">Three-Phase Approach</h3>
            <div className="phases-grid">
              <div className="phase-card">
                <div className="phase-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    <path d="M9 12h6m-6 4h6"/>
                  </svg>
                </div>
                <h4 className="phase-title">Pre-Operative Period</h4>
                <p className="phase-description">
                  Reviewing cases, confirming diagnoses, and preparing detailed treatment 
                  plans for each child in collaboration with local medical staff.
                </p>
              </div>

              <div className="phase-card">
                <div className="phase-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
                    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
                    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
                  </svg>
                </div>
                <h4 className="phase-title">Operative Phase</h4>
                <p className="phase-description">
                  Performing complex pediatric surgery & urology procedures with our team of 
                  specialized surgeons, anesthesiologists, and support staff.
                </p>
              </div>

              <div className="phase-card">
                <div className="phase-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                  </svg>
                </div>
                <h4 className="phase-title">Post-Operative Care</h4>
                <p className="phase-description">
                  Ensuring follow-up and recovery progress under safe and appropriate 
                  conditions before we depart.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="values-section section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Our Core Values</span>
            <h2 className="section-title">What Drives Our Work</h2>
          </div>
          
          <div className="values-grid">
            <div className="value-card">
              <div className="value-number">01</div>
              <h3 className="value-title">Education & Collaboration</h3>
              <p className="value-description">
                Our surgeons and anesthesiologists work hand in hand with local medical 
                staff in a university hospital setting. We provide teaching, training, 
                and joint clinical work with residents and faculty, always in collaboration 
                with the local university.
              </p>
            </div>
            <div className="value-card">
              <div className="value-number">02</div>
              <h3 className="value-title">Long-Term Impact</h3>
              <p className="value-description">
                Our goal is not only to treat patients, but to strengthen local capacity 
                and build lasting mutual collaboration. In some cases, physicians from our 
                partner hospitals are invited to continue their training at the hospitals 
                of our mission surgeons.
              </p>
            </div>
            <div className="value-card">
              <div className="value-number">03</div>
              <h3 className="value-title">Equipment & Resources</h3>
              <p className="value-description">
                We help supply essential medical instruments and equipment that are often 
                unaffordable for the hospitals we support. Thanks to healthcare companies 
                and partners, we receive high-value instruments for our partner institutions.
              </p>
            </div>
            <div className="value-card">
              <div className="value-number">04</div>
              <h3 className="value-title">Transparency & Accountability</h3>
              <p className="value-description">
                We operate with complete transparency to show where we work, whom we serve, 
                highlight our medical and educational activities, and acknowledge our 
                partners and supporters.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="partnership-section section">
        <div className="container">
          <div className="partnership-content">
            <h2 className="partnership-title">Partnership with National Pediatric Hospital, Phnom Penh</h2>
            <p className="partnership-text">
              Our close collaboration with the National Pediatric Hospital in Phnom Penh, 
              Cambodia, is the cornerstone of our work. This partnership ensures that we 
              work alongside local experts who understand their patients' needs and can 
              provide continuity of care after our missions conclude.
            </p>
            <p className="partnership-text">
              The hospital's medical team evaluates children throughout the year, identifying 
              those with complex conditions requiring specialized surgical intervention. These 
              cases are carefully reviewed and selected for our missions, ensuring we can 
              provide the greatest impact with our specialized expertise and resources.
            </p>
          </div>
        </div>
      </section>

      <section className="join-section section">
        <div className="container">
          <div className="join-card">
            <h2 className="join-title">Join Us in Transforming Young Lives</h2>
            <p className="join-description">
              Whether you're a healthcare professional looking to volunteer, someone who 
              wants to support our mission financially, or a company that can provide 
              medical equipment and supplies, there's a place for you in the DOC 
              Humanitarian family.
            </p>
            <div className="join-actions">
              <a href="/contact" className="btn btn-primary">Get Involved</a>
              <a href="/" className="btn btn-secondary">Back to Home</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
